<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import ModalBase from "@/components/modal/ModalBase.vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import PlanWizardProgress from "@/views/plans/components/wizard/PlanWizardProgress.vue";
import PlanWizardStepDate from "@/views/plans/components/wizard/PlanWizardStepDate.vue";
import PlanWizardStepVehicles from "@/views/plans/components/wizard/PlanWizardStepVehicles.vue";
import PlanWizardStepFinalize from "@/views/plans/components/wizard/PlanWizardStepFinalize.vue";
import { planStore } from "@/_store/planStore";
import { accountStore } from "@/_store/accountStore";
import { dateUtils, errorUtils } from "@/utils";

const emit = defineEmits(["close", "success"]);

const formSteps = 2;
const activeStep = ref(0);
const nameManuallyEdited = ref(false);

const vuexStore = useStore();
const bootstrap = vuexStore.state.bootstrap;
const plannerStore = planStore();
const accountsStore = accountStore();

const modalRef = ref(null);
const snackbar = ref(null);
const loadingVehicles = ref(false);
const disableButton = ref(false);

const todayQueryDate = computed(() =>
  dateUtils.formatDate(new Date(), "YYYY-MM-DD")
);

function buildDefaultPlanName(date, type) {
  const normalizedDate = String(date || "").split("T")[0] || todayQueryDate.value;
  const normalizedType = String(type || "").toLowerCase();

  if (normalizedType === "ambos") {
    return `Plan_${normalizedDate}`;
  }

  if (normalizedType === "recogida") {
    return `Recogida ${normalizedDate}`;
  }

  if (normalizedType === "entrega") {
    return `Entrega ${normalizedDate}`;
  }

  return `Plan ${normalizedDate}`;
}

const form = ref({
  date: todayQueryDate.value,
  type: "RECOGIDA",
  name: buildDefaultPlanName(todayQueryDate.value, "RECOGIDA"),
  close_open_previous_plans: false,
  vehicles_ids: [],
});

const rules = {
  date: {
    required: helpers.withMessage("Fecha es requerida", required),
  },
  name: {
    required: helpers.withMessage("Nombre es requerido", required),
  },
  vehicles_ids: {
    required: helpers.withMessage("Debe seleccionar al menos un vehículo", required),
  },
};

const v$ = useVuelidate(rules, form);

 

const closePreviousPlansOption = computed({
  get() {
    return form.value.close_open_previous_plans ? "SI" : "NO";
  },
  set(value) {
    form.value.close_open_previous_plans = value === "SI";
  },
});

function applyAccountDefaultPlanType() {
  const defaultType = accountsStore.account?.preferences?.plans?.default_plan_type;
  if (defaultType) form.value.type = defaultType;
}

onMounted(() => {
  plannerStore.clearCreateState();
  const modal = new bootstrap.Modal(document.getElementById("modalPlan"), {
    keyboard: true,
    backdrop: "static",
  });
  modal.show();

  if (accountsStore.account) {
    applyAccountDefaultPlanType();
  } else {
    accountsStore.getAccount().then(applyAccountDefaultPlanType).catch(() => null);
  }
});

function closeModal() {
  emit("close");
}

function closeSnackbar() {
  snackbar.value = null;
}

function nextStep() {
  if (activeStep.value < formSteps) {
    activeStep.value += 1;
  }
}

function prevStep() {
  if (activeStep.value > 0) {
    activeStep.value -= 1;
  }
}

function formatValidationErrors(errors) {
  let message = "";

  for (const error of errors) {
    if (!message.includes(`<li>${error.$message}</li>`)) {
      message = `${message}<li>${error.$message}</li>`;
    }
  }

  return `<ul>${message}</ul>`;
}

function showValidationErrors(errors) {
  snackbar.value = {
    title: "Error",
    message: formatValidationErrors(errors),
    type: "danger",
    color: "white",
  };
}

async function validate() {
  await v$.value.$validate();
  return v$.value.$errors;
}

async function validateFields(fields = []) {
  const errors = [];

  for (const field of fields) {
    const fieldRef = v$.value?.[field];
    if (!fieldRef) continue;

    await fieldRef.$validate();
    if (fieldRef.$error) {
      errors.push(...fieldRef.$errors);
    }
  }

  if (errors.length > 0) {
    showValidationErrors(errors);
  }

  return errors;
}

async function goToVehiclesStep() {
  const errors = await validateFields(["date"]);
  if (errors.length > 0) return;

  nextStep();
}

async function goToFinalizeStep() {
  const errors = await validateFields(["vehicles_ids"]);
  if (errors.length > 0) return;

  nextStep();
}

function toggleVehicleSelection(vehicleId, checked) {
  const normalizedId = String(vehicleId || "");
  if (!normalizedId) return;

  const selectedIds = new Set(
    (Array.isArray(form.value.vehicles_ids) ? form.value.vehicles_ids : []).map((id) =>
      String(id)
    )
  );

  if (checked) {
    selectedIds.add(normalizedId);
  } else {
    selectedIds.delete(normalizedId);
  }

  form.value.vehicles_ids = [...selectedIds];
}

function syncDefaultPlanName() {
  if (nameManuallyEdited.value) return;
  form.value.name = buildDefaultPlanName(form.value.date, form.value.type);
}

async function savePlan() {
  const errors = await validate();
  if (errors.length > 0) {
    showValidationErrors(errors);
    return;
  }

  disableButton.value = true;
  plannerStore.createPlan({
    date: form.value.date,
    type: form.value.type,
    name: form.value.name,
    close_open_previous_plans: form.value.close_open_previous_plans,
    vehicles_ids: form.value.vehicles_ids,
  });
}

watch(
  () => form.value.date,
  (value) => {
    if (!value) return;

    syncDefaultPlanName();
  }
);

watch(
  () => form.value.name,
  (value) => {
    if (value !== buildDefaultPlanName(form.value.date, form.value.type)) {
      nameManuallyEdited.value = true;
    }
  }
);

watch(
  () => form.value.type,
  () => {
    syncDefaultPlanName();
  }
);

watch(
  () => plannerStore.planCreated,
  (value) => {
    if (!value) return;
    disableButton.value = false;
    const planId = value._id;
    const planName = value.name;
    plannerStore.clearCreateState();
    modalRef.value.closeModal();
    emit("success", { _id: planId, name: planName });
  }
);

watch(
  () => plannerStore.error,
  (value) => {
    if (!value) return;
    disableButton.value = false;
    snackbar.value = {
      title: "Error",
      message: errorUtils.getErrorMessageHtml(value, "No se pudo crear el plan"),
      type: "danger",
      color: "white",
    };
  }
);
</script>

<template>
  <modal-base
    id="modalPlan"
    ref="modalRef"
    width="42rem"
    body-variant="canvas"
    @hide="closeModal"
  >
    <template #header>
      <h6 class="modal-title mb-0">Nuevo</h6>
    </template>

    <template #body>
      <div class="multisteps-form">
        <plan-wizard-progress :active-step="activeStep" />

        <div class="row">
          <div class="m-auto col-12">
            <form class="multisteps-form__form card-detail-layout text-start">
              <plan-wizard-step-date
                v-show="activeStep === 0"
                :date="form.date"
                :type="form.type"
                :today-query-date="todayQueryDate"
                :date-error="v$.date.$error"
                @update:date="form.date = $event"
                @update:type="form.type = $event"
                @next="goToVehiclesStep"
              />

              <plan-wizard-step-vehicles
                v-show="activeStep === 1"
                :date="form.date"
                :selected-vehicle-ids="form.vehicles_ids"
                @prev="prevStep"
                @next="goToFinalizeStep"
                @toggle-vehicle="toggleVehicleSelection"
                @loading-change="loadingVehicles = $event"
                @sync-selected-vehicles="form.vehicles_ids = $event"
              />

              <plan-wizard-step-finalize
                v-show="activeStep === 2"
                :name="form.name"
                :name-error="v$.name.$error"            
                :close-previous-plans-option="closePreviousPlansOption"
                :disable-button="disableButton"
                :loading-vehicles="loadingVehicles"
                @update:name="form.name = $event"         
                @update:close-previous-plans-option="closePreviousPlansOption = $event"
                @prev="prevStep"
                @save="savePlan"
              />
            </form>
          </div>
        </div>
      </div>
    </template>

    <div class="position-fixed top-0 end-0 z-index-2 p-3">
      <argon-snackbar
        v-if="snackbar"
        :title="snackbar.title"
        :description="snackbar.message"
        :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
        :color="snackbar.color"
        :close-handler="closeSnackbar"
      />
    </div>
  </modal-base>
</template>
